import { useState } from 'react'

import { useForm } from 'react-hook-form'

import { useTranslation } from 'react-i18next'

import { zodResolver } from '@hookform/resolvers/zod'

import { AlertCircle } from 'lucide-react'

import apiClient from '@/api/axios'
import { API_ENDPOINTS } from '@/api/endpoints'

import { toInputDate } from '@/lib/date'
import { logger } from '@/lib/logger'

import { COUNTRY_FIELD_DEFAULTS, PHONE_FIELD_DEFAULTS } from '@/config/formDefaults'

import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form'

import { CivilIDField } from '@/components/form-fields/CivilIDField'
import { CountryField } from '@/components/form-fields/CountryField'
import { DatePickerField } from '@/components/form-fields/DatePickerField'
import { EmailField } from '@/components/form-fields/EmailField'
import { PhoneField } from '@/components/form-fields/PhoneField'
import { TextField } from '@/components/form-fields/TextField'
import { FormRow } from '@/components/form-layouts/FormRow'
import { SmartButton } from '@/components/ui-custom/SmartButton'

import { accountSchema, type accountSchemaValues } from '@/schemas/auth/account.schema'

import toast from '../toast'

interface AccountFormProps {
  branchId: string | null
  planId: string | null
  onSuccess: () => void
  onBack?: () => void
}

export function renderFormMessages(messages: React.ReactNode[]) {
  if (!messages || messages.length === 0) return null

  return (
    <div className='mb-3 flex flex-col gap-2 text-sm text-red-700'>
      {messages.map((msg, index) => (
        <div key={index} className='flex items-center gap-3 rounded-md bg-red-50 px-3 py-2 shadow-sm'>
          <AlertCircle className='h-4 w-4 flex-shrink-0 text-red-500' />
          <span>{msg}</span>
        </div>
      ))}
    </div>
  )
}

export function AccountForm({ branchId, planId, onSuccess, onBack }: AccountFormProps) {
  const { i18n, t } = useTranslation(['auth', 'common'])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formMessages, setFormMessages] = useState<React.ReactNode[]>([])

  const addFormMessage = (message: React.ReactNode) => {
    setFormMessages((prev) => [...prev, message])
  }

  const clearFormMessages = () => {
    setFormMessages([])
  }

  const form = useForm<accountSchemaValues>({
    resolver: zodResolver(accountSchema),
    defaultValues: {
      phone: '',
      country: '',
      optin: true,
    },
  })

  const onSubmit = async (data: accountSchemaValues) => {
    clearFormMessages()
    setIsSubmitting(true)

    try {
      const payload = {
        ...data,
        branch_id: branchId,
        plan_id: planId,
        birth_date: data.birth_date ? toInputDate(data.birth_date) : null,
        civil_id: data.civil_id ? data.civil_id.replace(/\D/g, '') : null,
      }

      const response = await apiClient.post(API_ENDPOINTS.register.validate, payload)
      const { token } = response.data

      localStorage.setItem('registration_token', token)
      logger.debug(API_ENDPOINTS.register.validate, response.data)

      toast.success(
        t('auth:createAccountForm.success', {
          defaultValue: 'Account information saved successfully',
        })
      )

      onSuccess()
    } catch (error: any) {
      logger.error('Registration error:', error)

      const errorMessage = error.response?.data?.errorMessage
        ? error.response.data.errorMessage
        : t('auth:createAccountForm.error', {
            defaultValue: 'Something went wrong. Please try again.',
          })
      addFormMessage(errorMessage)
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card className='mx-auto max-w-xl rounded-2xl border-0 p-8 shadow-lg'>
      <div className='mb-4 text-center'>
        <h2 className='text-primary text-2xl font-bold'>{t('auth:createAccountForm.title', 'Create Your Account')}</h2>
        <p className='text-muted-foreground mt-2 text-sm'>
          {t('auth:createAccountForm.subtitle', {
            defaultValue: 'Please fill in your details to continue',
          })}
        </p>
      </div>
      {renderFormMessages(formMessages)}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-5'>
          <FormRow columns={2}>
            {/* First Name */}
            <TextField
              form={form}
              name='first_name'
              label={t('common:fields.firstName.label', 'First Name')}
              placeholder={t('common:fields.firstName.placeholder', 'First Name')}
              tooltip={t(
                'common:fields.firstName.tooltip',
                'Enter your given name as it appears on official documents.'
              )}
              required
            />
            {/* Last Name */}
            <TextField
              form={form}
              name='last_name'
              label={t('common:fields.lastName.label', 'Last Name')}
              placeholder={t('common:fields.lastName.placeholder', 'Last Name')}
              tooltip={t('common:fields.lastName.tooltip', 'Enter your family name or surname.')}
              required
            />
          </FormRow>
          <FormRow columns={1}>
            {/* Birthday */}
            <DatePickerField
              form={form}
              name='birth_date'
              label={t('common:fields.birthDate.label', 'Birth Date')}
              tooltip={t(
                'common:fields.birthDate.tooltip',
                'Select your date of birth. You must be at least 18 years old.'
              )}
              placeholder={t('common:fields.birthDate.placeholder', 'Pick Birthday Date')}
              required
            />
          </FormRow>

          {/* Email */}
          <EmailField
            form={form}
            name='email'
            label={t('common:fields.email.label', 'Email')}
            placeholder={'example@domain.com'}
            tooltip={t('common:fields.email.tooltip', 'We will send a confirmation link to this email address.')}
            required
          />
          {/* Phone */}
          <PhoneField
            form={form}
            name='phone'
            label={t('common:fields.phone.label', 'Phone Number')}
            placeholder={t('common:fields.phone.placeholder', 'Enter a phone number')}
            tooltip={t('common:fields.phone.tooltip', 'Include your country code (e.g., +965).')}
            defaultCountry={PHONE_FIELD_DEFAULTS.defaultCountry}
            preferredCountries={[...PHONE_FIELD_DEFAULTS.preferredCountries]}
            onlyCountries={[...PHONE_FIELD_DEFAULTS.onlyCountries]}
            required
          />

          {/* Civil ID */}
          <CivilIDField
            form={form}
            name='civil_id'
            label={t('common:fields.civilId.label', 'Civil ID')}
            tooltip={t('common:fields.civilId.tooltip', 'Enter your national identification number (12 digits).')}
            placeholder={t('common:fields.civilId.placeholder', '0000-0000-0000')}
            required
          />

          <FormRow columns={2}>
            {/* Country */}
            <CountryField
              form={form}
              name='country'
              label={t('common:fields.country.label', 'Country')}
              tooltip={t('common:fields.country.tooltip', 'Enter your country.')}
              onlyCountries={[...COUNTRY_FIELD_DEFAULTS.onlyCountries]}
              preferredCountries={[...COUNTRY_FIELD_DEFAULTS.preferredCountries]}
              locale={i18n.language}
              required
            />

            {/* City */}
            <TextField
              form={form}
              name='city'
              label={t('common:fields.city.label', 'City')}
              placeholder={t('common:fields.city.placeholder', 'Kuwait City')}
              tooltip={t('common:fields.city.tooltip', 'Enter your city.')}
              required
            />
          </FormRow>

          <FormField
            control={form.control}
            name='optin'
            render={({ field }) => (
              <FormItem className='flex items-center space-x-2 pt-2'>
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className='data-[state=checked]:bg-primary h-5 w-5 rounded-sm border-2'
                  />
                </FormControl>
                <FormLabel className='cursor-pointer text-sm leading-none font-normal'>
                  {t('common:fields.optin.label', 'Receive updates & offers')}
                </FormLabel>
              </FormItem>
            )}
          />
          {renderFormMessages(formMessages)}
          <div className='flex flex-col items-center gap-4'>
            <SmartButton
              variant='default'
              type='submit'
              loading={isSubmitting}
              disabled={isSubmitting}
              className='w-full'
            >
              {t('common:actions.continue', 'Continue')}
            </SmartButton>
            <SmartButton
              variant={'link'}
              onClick={onBack}
              type='button'
              className='cursor-pointer text-sm text-gray-600 transition-all hover:text-gray-800'
            >
              {t('common:actions.backLink', 'Back')}
            </SmartButton>
          </div>
        </form>
      </Form>
    </Card>
  )
}
